/** Read plain text from clipboard (never rich HTML). */
export function getPlainTextFromClipboard(data: DataTransfer | null): string {
  if (!data) return "";
  const plain = data.getData("text/plain");
  if (plain) return plain;

  const html = data.getData("text/html");
  if (!html) return "";

  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
}

/** Strip HTML, entities, markdown headings, and line breaks for single-line subject fields. */
export function normalizeSubjectPasteText(raw: string): string {
  let text = raw
    .replace(/\r\n/g, "\n")
    .replace(/<[^>]*>/g, "")
    .trim();

  if (!text) return "";

  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  text = textarea.value;

  return text
    .split("\n")
    .map(line => line.replace(/^\s*#{1,6}\s+/, "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Insert pasted plain text into a controlled <input> (works with React). */
export function insertTextIntoInput(
  input: HTMLInputElement,
  text: string,
  maxLength?: number
): void {
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  let next = input.value.slice(0, start) + text + input.value.slice(end);
  if (maxLength != null) {
    next = next.slice(0, maxLength);
  }

  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  setter?.call(input, next);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

export function isSubjectInputElement(el: EventTarget | null): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.type === "password" || el.type === "hidden") return false;

  const formLabel =
    el.closest(".ant-formily-item, .ant-form-item, [class*='form-item']")
      ?.querySelector("label")
      ?.textContent ?? "";

  const hint = [
    el.id,
    el.name,
    el.getAttribute("aria-label"),
    el.placeholder,
    el.closest("[data-field]")?.getAttribute("data-field"),
    formLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return hint.includes("subject");
}

export function handleSubjectInputPaste(
  e: React.ClipboardEvent<HTMLInputElement>,
  maxLength?: number
): void {
  e.preventDefault();
  const pasted = normalizeSubjectPasteText(
    getPlainTextFromClipboard(e.clipboardData)
  );
  if (!pasted) return;
  insertTextIntoInput(e.currentTarget, pasted, maxLength);
}
